<script setup lang="ts">
import { SellingMethod, useApiGetRelatedAuctions, useApiGetRelatedDirectSales } from '~/apiClient';
import { type SaleItemEntity } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const apiParams = computed(() => {
    switch (sale.sellingMethod) {
        case SellingMethod.Auction:
            return { auctionId: sale.id };
        case SellingMethod.DirectSale:
            return { directSaleId: sale.id };
    }
});

const { data: relatedAuctions, suspense: suspensReleatedAuctions } = useApiGetRelatedAuctions(apiParams);
const { data: relatedDirectSales, suspense: suspensReleatedDirectSales } = useApiGetRelatedDirectSales(apiParams);
const { setTrackingReferralListName, trackSelectSale } = useTracking();

await suspensReleatedAuctions();
await suspensReleatedDirectSales();

// DATA
const { sales: auctions } = useSaleListing({
    sales: toRef(() => relatedAuctions.value?.auctions ?? []),
    sellingMethod: SellingMethod.Auction,
});
const { sales: directSales } = useSaleListing({
    sales: toRef(() => relatedDirectSales.value?.directSales ?? []),
    sellingMethod: SellingMethod.DirectSale,
});
const auctionCarouselTitle = computed(() =>
    sale.sellingMethod === SellingMethod.Auction ? tt('sdp.Auction.related.auctions') : tt('sdp.DirectSale.related.auctions')
);
const directSaleCarouselTitle = computed(() =>
    sale.sellingMethod === SellingMethod.Auction ? tt('sdp.Auction.related.directSales') : tt('sdp.DirectSale.related.directSales')
);
const ui = {
    container: 'gap-x-4 p-1',
    item: 'w-64 basis-auto',
    prev: 'disabled:hidden',
    next: 'disabled:hidden',
};

// HANDLERS
const onNavigate = (): void => {
    trackSelectSale();
    setTrackingReferralListName('related');
};
</script>

<template>
    <div class="flex flex-col gap-6" :class="{ 'flex-col-reverse': sale.sellingMethod === SellingMethod.DirectSale }">
        <div v-if="auctions.length" class="flex flex-col gap-6">
            <h3 class="text-highlighted text-2xl font-semibold md:text-3xl">{{ auctionCarouselTitle }}</h3>
            <UCarousel v-slot="{ item }" :items="auctions" :ui="ui" arrows wheel-gestures>
                <SlpCard :sale="item" @navigate="onNavigate" />
            </UCarousel>
        </div>

        <div v-if="directSales.length" class="flex flex-col gap-6">
            <h3 class="text-highlighted text-2xl font-semibold md:text-3xl">{{ directSaleCarouselTitle }}</h3>
            <UCarousel v-slot="{ item }" :items="directSales" :ui="ui" arrows wheel-gestures>
                <SlpCard :sale="item" @navigate="onNavigate" />
            </UCarousel>
        </div>
    </div>
</template>
